import { claimFreePackage } from "@/API/offers";

export const freeClaim = {
  data() {
    return {
      claiming: false,
      // Set while a claimed offer waits for its modal hide transition to end.
      pendingOfferClear: false,
    };
  },
  methods: {
    // Normalize one asset's type (asset_type / assetType / type, or a PHP class
    // path like "App\\...\\Gold") to a lowercase reward key.
    assetTypeOf(asset) {
      if (asset.asset_type || asset.assetType || asset.type) {
        return (
          asset.asset_type ||
          asset.assetType ||
          asset.type
        ).toLowerCase();
      }
      if (asset.asset) {
        const className = asset.asset;
        const i = className.lastIndexOf("\\");
        return (i >= 0 ? className.substring(i + 1) : className).toLowerCase();
      }
      return "";
    },
    // The claim response carries the granted assets (random cards hold their
    // real id there); shape varies, so probe the known keys.
    extractClaimedAssets(claimResult) {
      if (!claimResult) return [];
      const candidates = [
        // The claim endpoint returns the booked assets under `result`.
        claimResult.result,
        claimResult.assets,
        claimResult.rewards,
        claimResult.granted,
        claimResult.items,
        claimResult.package && claimResult.package.assets,
      ];
      const found = candidates.find((c) => Array.isArray(c) && c.length);
      return found || [];
    },
    // Map package assets → the reward-array shape AnimationFullRewardEarnedAnimate
    // expects: [{ reward: "gold", amount }, { reward: "card", amount, cardId }].
    buildRewardAnimationArray(assets) {
      if (!Array.isArray(assets)) return [];
      const arr = [];
      assets.forEach((asset) => {
        const type = this.assetTypeOf(asset);
        const amount = parseInt(asset.amount, 10) || 0;
        if (!amount) return;
        switch (type) {
          case "gold":
          case "crown":
          case "loyalty":
          case "elixir":
          case "expbooster":
            arr.push({ reward: type, amount });
            break;
          case "card": {
            const cardId = asset.card_id ?? asset.cardId ?? asset.card?.id;
            if (cardId) arr.push({ reward: "card", amount, cardId });
            else arr.push({ reward: "cardpack", amount });
            break;
          }
        }
      });
      return arr;
    },
    // Resolve once the reward-earned animation queue has finished. The queue
    // sets `popups/queueAnimationIsRunning` true while playing and false when
    // done (see animation-base-functions). Wait for the running→idle edge; a
    // timeout fallback covers the case where the animation never starts (e.g.
    // runningAllowed() is false) so the caller still continues.
    waitForRewardAnimation() {
      return new Promise((resolve) => {
        const getRunning = () =>
          this.$store.getters["popups/getQueueAnimationIsRunning"];
        let started = getRunning();
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          unwatch();
          clearTimeout(fallback);
          resolve();
        };
        const unwatch = this.$store.watch(getRunning, (running) => {
          if (running) started = true;
          else if (started) finish();
        });
        // Fallback: if it hasn't started within 1.5s, stop waiting.
        const fallback = setTimeout(() => {
          if (!started) finish();
        }, 1500);
      });
    },
    // Play the full reward-earned animation for a finished claim and resolve
    // once it is done. Prefers the claim response's granted assets, falls back
    // to the package's declared assets.
    async playClaimRewardAnimation(claimResult, payPackage) {
      const grantedAssets = this.extractClaimedAssets(claimResult);
      const rewards = this.buildRewardAnimationArray(
        grantedAssets.length ? grantedAssets : payPackage && payPackage.assets
      );
      if (!rewards.length) return;
      this.AnimationFullRewardEarnedAnimate(rewards, false);
      await this.waitForRewardAnimation();
    },
    // The global response interceptor rejects with `error.response`, so status
    // and body sit at the top level; keep the raw axios shape as a fallback.
    claimErrorInfo(error) {
      const response = error && error.response ? error.response : error;
      const data = (response && response.data) || {};

      return {
        status: response && response.status,
        key: data.message || data.error || data.key || "",
        data,
      };
    },
    // Claim failures the user can act on get a specific message; everything
    // else falls back to the generic error banner.
    displayClaimError(error) {
      const { key } = this.claimErrorInfo(error);

      // Reuse the existing "rewards already collected" wording: a claim limit
      // is hit exactly when the user already booked this free package.
      if (key === "payoffer.error.claim_limit_reached") {
        this.$store.commit("popups/setAlertBannerContent", {
          alertText: this.$t("dailylogin.error.already_collected"),
          alertHeading: this.$t("alerts.title.error"),
          type: "danger",
          variant: "danger",
          dismissMessage: "ok-claim-limit-reached",
        });
        return;
      }

      this.displayErrorMessage();
    },
    // Drop a claimed offer's data once its modal is fully hidden. Call from the
    // modal's @hidden handler.
    flushPendingOfferClear() {
      if (!this.pendingOfferClear) return;
      this.pendingOfferClear = false;
      this.$store.commit("shop/clearGlobalOfferData");
    },
    // Claim a free package of a normal (non-chain) pay offer: book it, play the
    // reward animation, refresh the user and drop the offer from the UI.
    async claimPayOfferPackage(payPackage, offerId) {
      if (this.claiming) return;
      this.claiming = true;
      try {
        const claimResult = await claimFreePackage(payPackage.id, offerId);

        await this.playClaimRewardAnimation(claimResult, payPackage);

        this.$store.dispatch("user/updateUser");

        // Close the offer and drop its data: whether it stays claimable depends
        // on the package claim limit, so let the next offer fetch decide.
        // The data is cleared on @hidden (see flushPendingOfferClear), not
        // here: clearing it now flips the parent's v-if and tears the modal out
        // of the DOM mid-hide-transition, which throws inside Bootstrap.
        this.pendingOfferClear = true;
        this.closeModal();
      } catch (error) {
        console.error(
          "[FreeClaim][PayOffer] claim failed:",
          this.claimErrorInfo(error)
        );
        this.displayClaimError(error);
      } finally {
        this.claiming = false;
      }
    },
  },
};
